import { memo, useEffect, useMemo, type PropsWithChildren } from 'react';

import {
  countFavoritesInKnownSymbols,
  countPredictionMarketCardsFromLayouts,
} from '@src/components/PredictionMarkets/PredictionCard/predictionCardMixpanel';
import { useAppLayoutConfig } from '@src/providers/AppLayoutConfig/AppLayoutConfigContext';
import { usePredictionsContext } from '@src/providers/PredictionsProvider';
import { MixpanelMetadata, useFavoriteSecurities, useMixpanel } from '@talos/kyoko';

/**
 * Registers prediction-markets Mixpanel people metadata.
 * Must render under PredictionsProvider (and MixpanelProvider).
 * Mounted as its own provider layer (not inside PredictionsProvider) to avoid a circular import.
 */
export const PredictionMarketsMixpanelMetadata = memo(function PredictionMarketsMixpanelMetadata({
  children,
}: PropsWithChildren) {
  const mixpanel = useMixpanel();
  const { favoriteSecurities } = useFavoriteSecurities();
  const { events, eventSeries } = usePredictionsContext();
  const { layoutConfigs: tradingLayouts } = useAppLayoutConfig('trading');

  const knownSeriesSymbols = useMemo(
    () => new Set(eventSeries.map(series => series.SeriesSymbol).filter(Boolean)),
    [eventSeries]
  );
  const knownEventSymbols = useMemo(() => new Set(events.map(event => event.EventSymbol).filter(Boolean)), [events]);

  useEffect(() => {
    mixpanel.register({
      [MixpanelMetadata.NumberOfFavoriteEventSeries]: countFavoritesInKnownSymbols(
        favoriteSecurities,
        knownSeriesSymbols
      ),
      [MixpanelMetadata.NumberOfFavoriteEvents]: countFavoritesInKnownSymbols(favoriteSecurities, knownEventSymbols),
    });
  }, [mixpanel, favoriteSecurities, knownSeriesSymbols, knownEventSymbols]);

  useEffect(() => {
    const counts = countPredictionMarketCardsFromLayouts(tradingLayouts);
    mixpanel.register({
      [MixpanelMetadata.NumberOfPredictionMarketCardsForOutcome]: counts.forOutcome,
      [MixpanelMetadata.NumberOfPredictionMarketCardsForAllOutcomes]: counts.forAllOutcomes,
    });
  }, [mixpanel, tradingLayouts]);

  return children;
});
